/**
 * Holdouts — slice 61. A share of a campaign's people deliberately not sent
 * to, so the campaign's effect can be measured against people who were
 * otherwise identical.
 *
 * ## WHY A HOLDOUT, WHEN SLICE 60 ALREADY CREDITS CONVERSIONS
 *
 * Slice 60's Wend-sent attribution says a message came BEFORE a conversion. It
 * cannot say the message CAUSED it: a customer who orders every month converts
 * whether or not Wend writes to them. The difference between the conversion
 * rate of the people sent to and the people held back is the part the campaign
 * caused — the "incremental" conversions — and it is the only honest answer to
 * "did this campaign work?".
 *
 * ## WHO IS HELD BACK
 *
 * Chosen when the recipient list is built, deterministically: each person falls
 * in one of {@link HOLDOUT_BUCKETS} buckets by a hash of the campaign and the
 * person, and buckets below `percent × 100` are held back. The same campaign and
 * person always give the same answer, so a list built twice — or previewed, then
 * built — holds back the same people, and nobody can pick who is held.
 *
 * Only people who would otherwise be attempted: after suppression, resting AND
 * the send guard. A held-back person is one this campaign would really have
 * sent to, so the held group and the sent group are drawn from the same people.
 * (Slice 61 first put it before the guard; review found that on a server whose
 * allow-list holds a handful of addresses, the held group was a tenth of the
 * whole list while the sent group was the handful — two different populations.)
 *
 * ## THE TWO GROUPS, AND THE DAYS
 *
 * Sent: every person whose row was attempted — sent, delivered, bounced or
 * failed. Intent to treat: dropping the failures would compare the people a
 * provider accepted with everybody held back. The readout is drawn only for a
 * campaign that finished sending; a cancelled or failed one says why not. The
 * window runs from the first day a message went out to {@link
 * HOLDOUT_WINDOW_DAYS} days after the last, as Indian days.
 *
 * Off by default, per campaign: a cooperative bank may not want to withhold a
 * notice, and holding back is a choice made where the campaign is scheduled.
 *
 * ## THE READOUT IS A NUMBER ONLY WHEN IT CAN BE ONE
 *
 * {@link holdoutReadoutSchema} is a discriminated union, so "not enough people
 * held back to tell" is a state with no field a rate could sit in — never a
 * confident 0% lift. Matched outcomes only: a declared count has no person, so
 * it cannot be split into sent and held.
 */
import { z } from 'zod';

import { uuidSchema } from '../common.js';
import { calendarDateSchema } from './analytics.js';
import { ATTRIBUTION_WINDOW_DAYS } from './outcomes.js';

/* The shares a campaign may hold back are `HOLDOUT_PERCENT_VALUES`, in `campaign.ts`. */

/** Buckets a person may hash into; `percent × 100` of them are held back. */
export const HOLDOUT_BUCKETS = 10_000;

/** Fewest people in EACH group before a readout is drawn. Below it, the difference is noise. */
export const HOLDOUT_MIN_PEOPLE = 30;

/** Days after the campaign's day in which a conversion counts — the attribution window, reused. */
export const HOLDOUT_WINDOW_DAYS = ATTRIBUTION_WINDOW_DAYS;

/** `|z|` at or above this is called a real difference (two-sided, about 95%). */
export const HOLDOUT_Z_THRESHOLD = 1.96;

/**
 * Fewest conversions EXPECTED in each group (the pooled rate × the group's
 * size) before the z-test is trusted. Below it the normal approximation is
 * poor — 0 to 4 conversions among 40 people — and a difference is reported as
 * possibly chance, whatever z says.
 */
export const HOLDOUT_MIN_EXPECTED = 5;

const groupSchema = z
  .object({
    /** People in the group. */
    people: z.number().int().min(0),
  })
  .strict();

const windowSchema = z.object({ from: calendarDateSchema, to: calendarDateSchema }).strict();

export const holdoutResultSchema = z
  .object({
    sentConverted: z.number().int().min(0),
    heldConverted: z.number().int().min(0),
    /** 0..1. */
    sentRate: z.number().min(0).max(1),
    heldRate: z.number().min(0).max(1),
    /** (sentRate − heldRate) × sent people. May be negative; may be fractional. */
    incrementalConversions: z.number(),
    /** Paise the campaign added, by the same arithmetic over value per person. May be negative. */
    incrementalValueMinor: z.number().int(),
    /** Whether the difference clears {@link HOLDOUT_Z_THRESHOLD}. False means "could be chance". */
    significant: z.boolean(),
  })
  .strict();
export type HoldoutResult = z.infer<typeof holdoutResultSchema>;

/** `GET /sense/holdouts/campaigns/:campaignId`. */
export const holdoutReadoutSchema = z.discriminatedUnion('state', [
  /** The campaign holds nobody back. */
  z.object({ state: z.literal('off'), campaignId: uuidSchema }).strict(),
  /** Held back, but sending has not finished, or the window has not passed. */
  z
    .object({
      state: z.literal('waiting'),
      campaignId: uuidSchema,
      percent: z.number().int().min(1).max(50),
      sent: groupSchema,
      held: groupSchema,
      /** Null while the campaign is still sending: the window starts from its send days. */
      window: windowSchema.nullable(),
      /** The first day a readout can be drawn; null while the campaign is still sending. */
      readyOn: calendarDateSchema.nullable(),
    })
    .strict(),
  /** No list of who converted covers the window — or the campaign did not finish sending. */
  z
    .object({
      state: z.literal('not_measured'),
      campaignId: uuidSchema,
      percent: z.number().int().min(1).max(50),
      sent: groupSchema,
      held: groupSchema,
      window: windowSchema.nullable(),
      reason: z.string(),
      /** Whether uploading a list would fix it — the screen links to Outcomes only then. */
      fixedByUpload: z.boolean(),
    })
    .strict(),
  /** Too few people in a group to tell a difference from noise. */
  z
    .object({
      state: z.literal('too_few'),
      campaignId: uuidSchema,
      percent: z.number().int().min(1).max(50),
      sent: groupSchema,
      held: groupSchema,
      window: windowSchema.nullable(),
      floor: z.literal(HOLDOUT_MIN_PEOPLE),
    })
    .strict(),
  z
    .object({
      state: z.literal('measured'),
      campaignId: uuidSchema,
      percent: z.number().int().min(1).max(50),
      sent: groupSchema,
      held: groupSchema,
      window: windowSchema,
      result: holdoutResultSchema,
    })
    .strict(),
]);
export type HoldoutReadout = z.infer<typeof holdoutReadoutSchema>;

export const holdoutReadoutParamsSchema = z.object({ campaignId: uuidSchema }).strict();

/**
 * The two-proportion z statistic, pooled. Zero when it cannot be computed — no
 * conversions anywhere, or an empty group — because "no evidence of a
 * difference" is exactly what zero says.
 */
export function holdoutZ(sentConverted: number, sentPeople: number, heldConverted: number, heldPeople: number): number {
  if (sentPeople === 0 || heldPeople === 0) return 0;
  const pooled = (sentConverted + heldConverted) / (sentPeople + heldPeople);
  const variance = pooled * (1 - pooled) * (1 / sentPeople + 1 / heldPeople);
  if (variance <= 0) return 0;
  return (sentConverted / sentPeople - heldConverted / heldPeople) / Math.sqrt(variance);
}
